import React, { useRef, useEffect, useState, useLayoutEffect, useMemo, useCallback } from 'react';
import { FaChevronRight, FaChevronLeft, FaChevronUp, FaChevronDown, FaTimes } from 'react-icons/fa';
import lead1 from '../../images/1_lead.png';
import lead2 from '../../images/2_lead.png';
import lead3 from '../../images/3_lead.png';
import lead4 from '../../images/4_lead.png';
import lead5 from '../../images/5_lead.png';
import lead6 from '../../images/6_lead.png';
import lead7 from '../../images/7_lead.png';
import lead8 from '../../images/8_lead.png';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const teamMembers = [
    { 
        image: lead1, 
        name: "Daniel Templeman", 
        role: "Founder / Project Management Lead",
        certificates: [
            "Project Management Professional (PMP)",
            "Prince2 Foundation Level, 2027",
            "Prince2 Practitioner, 2027",
            "Certificate in Construction Project Management and Planning, 2019"
        ]
    },
    { 
        image: lead2, 
        name: "Ila Mikaela Gutierrez", 
        role: "Quantity Surveying & Estimating Lead",
        certificates: [
            "B.S. Quantity Surveying", 
            "Certified Professional Estimator (CPE)",
            "Advanced Contract Management Diploma"
        ] 
    },
    { 
        image: lead3, 
        name: "Emily Clarke", 
        role: "Administrative Support Lead",
        certificates: [
            "Certified Administrative Professional (CAP)",
            "Executive Assistant Certificate, 2020"
        ] 
    },
    { 
        image: lead4, 
        name: "James Bennett", 
        role: "Client Relations Specialist",
        certificates: [
            "Certified Customer Experience Professional (CCXP)",
            "Conflict Resolution Training, 2021"
        ] 
    },
    { 
        image: lead5, 
        name: "Leonie Templeman", 
        role: "Legal & Compliance Specialist",
        certificates: ["Juris Doctor (JD)", "Certified Compliance Officer (CCO)"] 
    },
    { 
        image: lead6, 
        name: "Sophia Rodriguez", 
        role: "Social Media & Content Strategist",
        certificates: [
            "Digital Marketing Professional Certification",
            "Advanced Content Strategy Workshop"
        ] 
    },
    { 
        image: lead7, 
        name: "Victor Chen", 
        role: "Procurement & Vendor Manager",
        certificates: [
            "Certified Professional in Supply Management (CPSM)",
            "Strategic Sourcing Expert, 2018"
        ] 
    },
    { 
        image: lead8, 
        name: "Olivia Kim", 
        role: "Marketing & Outreach Coordinator",
        certificates: [
            "Google Analytics Certification",
            "Event Planning and Management Diploma"
        ] 
    },
];

const SCROLL_STEP = 120;

const ExpandedMemberDetail = ({ member, onClose, cardRef, expandOrigin, isMobile = false, isTablet = false }) => {
    const [panelView, setPanelView] = useState('training');
    const isTrainingView = panelView === 'training';
    const [isVisible, setIsVisible] = useState(false);
    const [currentTeamIndex, setCurrentTeamIndex] = useState(0);
    const detailRef = useRef(null);
    const contentScrollRef = useRef(null);
    
    // Parse team members - convert string array to object array if needed
    const parsedTeamMembers = useMemo(() => {
        if (!member.team || member.team.length === 0) return [];
        
        return member.team.map((teamMember, index) => {
            // If it's already an object, use it
            if (typeof teamMember === 'object' && teamMember !== null) {
                return {
                    name: teamMember.name || `Team Member ${index + 1}`,
                    position: teamMember.position || teamMember.role || '',
                    description: teamMember.description || ''
                };
            }
            // If it's a string: "Name || Role || Description" or legacy "Role: Name"
            const str = String(teamMember).trim();
            const sep = ' || ';
            if (str.includes(sep)) {
                const parts = str.split(sep).map((p) => p.trim());
                return {
                    name: parts[0] || '',
                    position: parts[1] || '',
                    description: parts.slice(2).join(sep).trim() || ''
                };
            }
            const colonIndex = str.indexOf(':');
            if (colonIndex > 0) {
                return {
                    position: str.substring(0, colonIndex).trim(),
                    name: str.substring(colonIndex + 1).trim(),
                    description: ''
                };
            }
            return {
                name: str,
                position: '',
                description: ''
            };
        });
    }, [member.team]);
    
    const currentTeamMember = parsedTeamMembers[currentTeamIndex] || null;

    useEffect(() => {
        const frame = requestAnimationFrame(() => setIsVisible(true));
        return () => {
            cancelAnimationFrame(frame);
            setIsVisible(false);
        };
    }, []);

    useLayoutEffect(() => {
        if (isMobile || !detailRef.current || !expandOrigin) return;

        const card = detailRef.current;
        const finalRect = card.getBoundingClientRect();
        const finalCenterX = finalRect.left + finalRect.width / 2 + window.scrollX;
        const finalCenterY = finalRect.top + finalRect.height / 2 + window.scrollY;
        const originCenterX = expandOrigin.left + expandOrigin.width / 2;
        const originCenterY = expandOrigin.top + expandOrigin.height / 2;

        const scale = expandOrigin.width / finalRect.width;
        const translateX = originCenterX - finalCenterX;
        const translateY = originCenterY - finalCenterY;

        card.style.transition = 'none';
        card.style.transformOrigin = 'center center';
        card.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
        card.style.opacity = '0';
        card.style.willChange = 'transform, opacity';

        requestAnimationFrame(() => {
            card.style.transition = 'transform 0.55s cubic-bezier(0.4,0,0.2,1), opacity 0.35s ease-out';
            card.style.transform = 'translate(0px, 0px) scale(1)';
            card.style.opacity = '1';
        });

        const handleOpenEnd = (event) => {
            if (event.target === card && event.propertyName === 'transform') {
                card.style.willChange = '';
                card.removeEventListener('transitionend', handleOpenEnd);
            }
        };

        card.addEventListener('transitionend', handleOpenEnd);

        return () => {
            card.removeEventListener('transitionend', handleOpenEnd);
            card.style.willChange = '';
        };
    }, [expandOrigin, isMobile]);

    // Mobile-only: portrait full-screen style panel
    if (isMobile) {
        return (
            <div 
                ref={(node) => {
                    if (node) {
                        detailRef.current = node;
                        if (cardRef && typeof cardRef === 'object' && 'current' in cardRef) cardRef.current = node;
                    }
                }}
                data-people-behind-card="true"
                className={`mx-auto w-full max-w-md bg-[#040919]/95 backdrop-blur rounded-2xl overflow-hidden flex flex-col relative shadow-2xl ${
                    isVisible ? 'opacity-100' : 'opacity-0'
                }`}
                style={{ 
                    maxHeight: '90vh',
                    minHeight: '80vh',
                    transition: 'opacity 0.3s ease-out'
                }}
            >
                <button 
                    onClick={onClose} 
                    className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center bg-black/40 text-white rounded-full"
                    aria-label="Close"
                >
                    <FaTimes className="w-5 h-5" />
                </button>
                {/* Portrait: square pic + name card overlay (like reference) */}
                <div 
                    className="relative flex-shrink-0 w-full overflow-hidden flex items-center justify-center"
                    style={{ 
                        background: 'linear-gradient(180deg, #C4793C 0%, #C0783D 50%, #B96E35 78.83%, #AF602A 100%)',
                        aspectRatio: '1 / 1'
                    }}
                >
                    <img 
                        src={member.image} 
                        alt={member.name} 
                        className="w-full h-full object-cover object-center"
                        style={{ imageRendering: 'high-quality', objectPosition: 'center top' }}
                    />
                    <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm text-gray-900 px-4 py-3 rounded-2xl shadow-xl border border-white/50 flex flex-col items-center">
                        <h3 className="text-xl font-bold tracking-tight text-black">{member.name}</h3>
                        <p className="text-sm text-gray-700 font-medium mt-0.5">{member.role || member.profession}</p>
                    </div>
                </div>
                {/* Scrollable content: Training / Team tabs */}
                <div className="flex-1 flex flex-col min-h-0 overflow-hidden p-4 pb-6">
                    <h2 className="text-xl font-bold text-[#E68A40] mb-3">
                        {isTrainingView ? 'Training and Certificates' : 'TEAM'}
                    </h2>
                    {isTrainingView ? (
                        <div className="flex-1 overflow-y-auto min-h-0 rounded-lg border border-white/10 bg-white/5 p-4">
                            {member.certificates && member.certificates.length > 0 ? (
                                <ul className="space-y-3 text-sm text-white/90">
                                    {member.certificates.map((cert, i) => (
                                        <li key={i} className="flex items-start gap-2">
                                            <span className="text-[#E68A40] flex-shrink-0">•</span>
                                            <span className="leading-relaxed">{cert}</span>
                                        </li>
                                    ))}
                                </ul>
                            ) : member.bio ? (
                                <div className="text-sm text-white/90 prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: member.bio }} />
                            ) : (
                                <p className="text-white/60 italic">No certificates listed</p>
                            )}
                        </div>
                    ) : (
                        <div className="flex-1 overflow-y-auto min-h-0 rounded-lg border border-white/10 bg-white/5 p-4">
                            <div className="space-y-4">
                                {currentTeamMember ? (
                                    <>
                                        <h3 className="text-lg font-bold text-white">{currentTeamMember.name}</h3>
                                        {currentTeamMember.position && (
                                            <p className="text-xs text-white/60 uppercase tracking-wider">{currentTeamMember.position}</p>
                                        )}
                                        {currentTeamMember.description ? (
                                            <div className="text-sm text-white/80 leading-relaxed prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: currentTeamMember.description }} />
                                        ) : (
                                            <p className="text-white/40 italic text-sm">No description</p>
                                        )}
                                    </>
                                ) : (
                                    <p className="text-white/60 italic text-sm">No team members assigned</p>
                                )}
                            </div>
                        </div>
                    )}
                    {/* Tab switcher */}
                    <div className="flex items-center gap-2 mt-4 flex-shrink-0">
                        <button
                            onClick={() => { setPanelView('training'); setCurrentTeamIndex(0); }}
                            className={`flex-1 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wider ${isTrainingView ? 'bg-[#E68A40] text-white' : 'bg-white/10 text-white'}`}
                        >
                            Certificates
                        </button>
                        <button
                            onClick={() => { setPanelView('team'); setCurrentTeamIndex(0); }}
                            className={`flex-1 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wider ${!isTrainingView ? 'bg-[#E68A40] text-white' : 'bg-white/10 text-white'}`}
                        >
                            Team
                        </button>
                    </div>
                    {!isTrainingView && parsedTeamMembers.length > 0 && (
                        <div className="flex gap-1.5 mt-2 flex-wrap justify-center">
                            {parsedTeamMembers.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentTeamIndex(index)}
                                    className={`w-8 h-8 rounded-lg text-xs font-medium ${currentTeamIndex === index ? 'bg-[#E68A40] text-white' : 'bg-white/10 text-white'}`}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Desktop: original side-by-side layout and expand animation
    return (
        <div 
            ref={(node) => {
                if (node) {
                    detailRef.current = node;
                    if (cardRef) {
                        if (typeof cardRef === 'function') {
                            cardRef(node);
                        } else if ('current' in cardRef) {
                            cardRef.current = node;
                        }
                    }
                }
            }}
            data-people-behind-card="true"
            className={`mx-auto bg-[#040919]/90 backdrop-blur rounded-[32px] overflow-hidden flex flex-col lg:flex-row relative shadow-[0_20px_45px_rgba(0,0,0,0.45)] transform transition-all duration-700 ease-[cubic-bezier(0.19,1,0.22,1)] ${
                isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-90 translate-y-8'
            }`}
            style={{
                transformOrigin: 'center center',
                width: isTablet ? '100vw' : 'calc(100% - 566px)',
                maxWidth: isTablet ? 'none' : 'calc(100% - 566px)',
                marginLeft: isTablet ? 'calc(-50vw + 50%)' : undefined,
                marginRight: isTablet ? 0 : undefined,
                height: isTablet ? 'auto' : 'calc(100vh - 200px - 200px)',
                maxHeight: isTablet ? 'calc(100vh - 200px)' : 'calc(100vh - 200px - 200px)',
                padding: isTablet ? '24px 0 28px 0' : '0px',
                boxSizing: isTablet ? 'border-box' : undefined,
            }}
        >
            <button 
                onClick={() => {
                    if (!detailRef.current || !expandOrigin) {
                        onClose();
                        return;
                    }
                    const card = detailRef.current;
                    const finalRect = card.getBoundingClientRect();
                    const finalCenterX = finalRect.left + finalRect.width / 2 + window.scrollX;
                    const finalCenterY = finalRect.top + finalRect.height / 2 + window.scrollY;
                    const originCenterX = expandOrigin.left + expandOrigin.width / 2;
                    const originCenterY = expandOrigin.top + expandOrigin.height / 2;

                    const scale = expandOrigin.width / finalRect.width;
                    const translateX = originCenterX - finalCenterX;
                    const translateY = originCenterY - finalCenterY;

                    card.style.willChange = 'transform, opacity';
                    card.style.pointerEvents = 'none';
                    card.style.transition = 'transform 0.5s cubic-bezier(0.4,0,0.2,1), opacity 0.3s ease-in';
                    card.style.transformOrigin = 'center center';
                    card.style.opacity = '1';

                    requestAnimationFrame(() => {
                        card.style.opacity = '0';
                        card.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
                    });

                    const handleClose = (event) => {
                        if (event.target !== card || event.propertyName !== 'transform') return;
                        card.removeEventListener('transitionend', handleClose);
                        card.style.willChange = '';
                        card.style.pointerEvents = '';
                        onClose();
                    };

                    card.addEventListener('transitionend', handleClose);
                }} 
                className={`absolute top-6 right-6 z-10 w-10 h-10 flex items-center justify-center text-[#E68A40] rounded-full transition-all duration-300 focus:outline-none hover:rotate-90`}
                aria-label="Close"
            >
                <FaTimes className="w-6 h-6 drop-shadow-lg" />
            </button>
            
            <div 
                className={`lg:w-[55%] w-full relative overflow-hidden flex items-center justify-center animate-[slideInLeft_0.7s_ease-out] ${isTablet ? 'min-h-0' : 'h-full'}`}
                style={isTablet ? { aspectRatio: '1 / 1' } : {}}
            >
                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, #C4793C 0%, #C0783D 50%, #B96E35 78.83%, #AF602A 100%)' }}></div>
                <div 
                    className="relative w-full h-full flex items-center justify-center"
                    style={{
                        padding: isTablet ? 0 : '2.5rem 3.5rem',
                    }}
                >
                    <img 
                        src={member.image} 
                        alt={member.name} 
                        className={`w-full ${isTablet ? 'h-full object-cover object-center' : 'h-full object-contain object-center'} transition-transform duration-700`}
                        style={{ 
                            imageRendering: 'high-quality',
                            ...(isTablet ? { maxHeight: '100%', objectPosition: 'center top' } : {}),
                        }}
                    />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#050910]/80 via-transparent to-transparent pointer-events-none"></div>
                <div className={`absolute bg-white/95 backdrop-blur-sm text-gray-900 shadow-xl border border-white/50 flex flex-col items-center ${isTablet ? 'bottom-4 left-4 right-4 px-4 py-3 rounded-2xl' : 'bottom-6 left-8 right-8 px-6 py-4 rounded-2xl'}`}>
                    <h3 className="text-2xl font-bold tracking-tight text-black">{member.name}</h3>
                    <p className="mt-1 text-sm text-gray-700 font-semibold">{member.role || member.profession}</p>
                </div>
            </div>

            <div 
                className="relative lg:w-[45%] w-full h-full p-8 lg:p-16 flex flex-col overflow-hidden animate-[slideInRight_0.7s_ease-out]"
            >
                <div 
                    className={`absolute inset-0 transition-opacity duration-500 ${isTrainingView ? 'opacity-100' : 'opacity-0'}`}
                    style={{
                        background: 'linear-gradient(0deg, rgba(255, 255, 255, 0.0509804), rgba(255, 255, 255, 0.0509804))'
                    }}
                />
                <div 
                    className={`absolute inset-0 transition-opacity duration-500 ${!isTrainingView ? 'opacity-100' : 'opacity-0'}`}
                    style={{
                        background: 'linear-gradient(0deg, rgba(255, 255, 255, 0.101961), rgba(255, 255, 255, 0.101961))'
                    }}
                />
                <div key={panelView} className="relative z-10 transition-all duration-500 ease-out animate-[fadeIn_0.5s_ease-out] flex flex-col h-full">
                    <h2 className="text-3xl sm:text-4xl font-bold text-[#E68A40] mb-6">
                        {isTrainingView ? 'Training and Certificates' : 'TEAM'}
                    </h2>
                    {isTrainingView ? (
                        <div className="relative flex-1 flex flex-col min-h-0 mb-6">
                            <div ref={contentScrollRef} className="flex-1 bg-white/5 rounded-lg border border-white/10 p-6 overflow-y-auto min-h-0 pr-12">
                                {member.certificates && member.certificates.length > 0 ? (
                                    <ul className="space-y-4 text-base text-white/90">
                                        {member.certificates.map((cert, i) => (
                                            <li 
                                                key={i} 
                                                className="flex items-start gap-3 transition-all duration-300"
                                                style={{ animation: `fadeInRight 0.4s ease-out ${200 + i * 80}ms both` }}
                                            >
                                                <span className="mt-1 text-[#E68A40] text-lg flex-shrink-0">•</span>
                                                <span className="leading-relaxed">{cert}</span>
                                            </li>
                                        ))}
                                    </ul>
                                ) : member.bio ? (
                                    <div 
                                        className="text-lg text-white/90 prose prose-invert max-w-none"
                                        dangerouslySetInnerHTML={{ __html: member.bio }}
                                        style={{
                                            color: 'white',
                                        }}
                                    />
                                ) : (
                                    <p className="text-white/60 italic">No certificates listed</p>
                                )}
                            </div>
                            <div className="hidden lg:flex absolute right-3 top-1/2 -translate-y-1/2 flex-col gap-1 z-10 pointer-events-none">
                                <button
                                    type="button"
                                    onClick={() => contentScrollRef.current?.scrollBy({ top: -SCROLL_STEP, behavior: 'smooth' })}
                                    className="pointer-events-auto w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-[#E68A40] transition-colors"
                                    aria-label="Scroll up"
                                >
                                    <FaChevronUp className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => contentScrollRef.current?.scrollBy({ top: SCROLL_STEP, behavior: 'smooth' })}
                                    className="pointer-events-auto w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-[#E68A40] transition-colors"
                                    aria-label="Scroll down"
                                >
                                    <FaChevronDown className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="relative flex-1 flex flex-col min-h-0 mb-6">
                            <div ref={contentScrollRef} className="flex-1 bg-white/5 rounded-lg border border-white/10 p-6 overflow-y-auto min-h-0 pr-12">
                                {currentTeamMember ? (
                                    <div className="space-y-4">
                                        <h3 className="text-2xl sm:text-3xl font-bold text-white">
                                            {currentTeamMember.name}
                                        </h3>
                                        {currentTeamMember.position && (
                                            <div className="text-sm text-white/60 uppercase tracking-wider">
                                                {currentTeamMember.position}
                                            </div>
                                        )}
                                        {currentTeamMember.description ? (
                                            <div className="text-base text-white/80 leading-relaxed prose prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: currentTeamMember.description }} />
                                        ) : (
                                            <p className="text-base text-white/40 italic">No description</p>
                                        )}
                                    </div>
                                ) : (
                                    <p className="text-white/60 italic">No team members assigned</p>
                                )}
                            </div>
                            <div className="hidden lg:flex absolute right-3 top-1/2 -translate-y-1/2 flex-col gap-1 z-10 pointer-events-none">
                                <button
                                    type="button"
                                    onClick={() => contentScrollRef.current?.scrollBy({ top: -SCROLL_STEP, behavior: 'smooth' })}
                                    className="pointer-events-auto w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-[#E68A40] transition-colors"
                                    aria-label="Scroll up"
                                >
                                    <FaChevronUp className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => contentScrollRef.current?.scrollBy({ top: SCROLL_STEP, behavior: 'smooth' })}
                                    className="pointer-events-auto w-9 h-9 flex items-center justify-center rounded-lg bg-white/10 text-white hover:bg-[#E68A40] transition-colors"
                                    aria-label="Scroll down"
                                >
                                    <FaChevronDown className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Numbered buttons for team members - only show in team view, same row as GO BACK (z-20 para ma-click, above bottom bar) */}
                {!isTrainingView && parsedTeamMembers.length > 0 && (
                    <div className="absolute right-8 lg:right-16 bottom-8 z-20 flex items-center gap-1.5">
                        <div className="flex items-center gap-1.5 flex-wrap justify-end">
                            {parsedTeamMembers.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentTeamIndex(index)}
                                    className={`w-8 h-8 rounded-lg text-xs font-medium transition-all duration-300 ${
                                        currentTeamIndex === index
                                            ? 'bg-[#E68A40] text-white shadow-lg'
                                            : 'bg-white/10 text-white hover:bg-white/20'
                                    }`}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="absolute inset-x-0 bottom-0 z-10 flex items-center justify-between px-8 lg:px-16 pb-8">
                    <div className={`transition-all duration-300 ${isTrainingView ? 'opacity-0 pointer-events-none translate-y-2' : 'opacity-100 translate-y-0'}`}>
                        <button
                            onClick={() => {
                                setPanelView('training');
                                setCurrentTeamIndex(0); // Reset to first team member
                            }}
                            className="flex items-center gap-2 bg-white/90 text-[#0b1221] px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-[0.25em] shadow-lg hover:bg-white transition-all duration-300"
                        >
                            <FaChevronLeft className="w-4 h-4" />
                            go back
                        </button>
                    </div>
                    <div className={`transition-all duration-300 ${isTrainingView ? 'opacity-100 translate-y-0' : 'opacity-0 pointer-events-none translate-y-2'}`}>
                        <button
                            onClick={() => {
                                setPanelView('team');
                                setCurrentTeamIndex(0); // Reset to first team member
                            }}
                            className="flex items-center gap-2 bg-white/90 text-[#0b1221] px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-[0.25em] shadow-lg hover:bg-white transition-all duration-300"
                        >
                            view team
                            <FaChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Match CSS breakpoints
// - Mobile + Tablet (phone view layout): below 1368px (phones, tablets, iPads — same layout as phone)
// - Desktop only: 1368px and up
const MOBILE_BREAKPOINT = 768;
const DESKTOP_MIN_WIDTH = 1368; // Tablet/iPad use mobile layout below this

const getIsMobile = () => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches;
};

const getIsTablet = () => {
    if (typeof window === 'undefined') return false;
    const width = window.innerWidth;
    return width >= MOBILE_BREAKPOINT && width < DESKTOP_MIN_WIDTH;
};

const ThePeopleBehind = () => {
    const sectionRef = useRef(null);
    const [selectedMember, setSelectedMember] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [isMobile, setIsMobile] = useState(getIsMobile);
    const [isTablet, setIsTablet] = useState(getIsTablet);
    const [mobileIndex, setMobileIndex] = useState(0);
    const expandedCardRef = useRef(null);
    const cardRefs = useRef([]);
    const [expandOrigin, setExpandOrigin] = useState(null);
    const [expandingIndex, setExpandingIndex] = useState(null);

    // Mobile = phone; tablet = 768..1367. isMobileView = use phone layout (phone + tablet)
    const isMobileView = isMobile || isTablet;

    // Reliable mobile / tablet detection: matchMedia + resize/orientation
    useEffect(() => {
        const mobileMql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
        const updateMobile = () => setIsMobile(mobileMql.matches);

        const updateTablet = () => setIsTablet(getIsTablet());

        updateMobile();
        updateTablet();

        mobileMql.addEventListener('change', updateMobile);
        window.addEventListener('resize', updateMobile);
        window.addEventListener('resize', updateTablet);
        window.addEventListener('orientationchange', updateMobile);
        window.addEventListener('orientationchange', updateTablet);
        return () => {
            mobileMql.removeEventListener('change', updateMobile);
            window.removeEventListener('resize', updateMobile);
            window.removeEventListener('resize', updateTablet);
            window.removeEventListener('orientationchange', updateMobile);
            window.removeEventListener('orientationchange', updateTablet);
        };
    }, []);

    useEffect(() => {
        // Don't scroll when card is expanded - keep the section centered
        // The expanded card should appear within the current viewport
    }, [selectedMember]);

    useEffect(() => {
        if (!selectedMember) {
            setExpandingIndex(null);
        }
    }, [selectedMember]);

    // Fetch employees from backend (callable for retry)
    const fetchEmployees = useCallback(async () => {
        setFetchError(null);
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/employees`);
            const data = await response.json();
            
            if (data.success) {
                const sortedEmployees = data.employees
                    .sort((a, b) => {
                        if (a.position !== undefined && b.position !== undefined) {
                            return a.position - b.position;
                        }
                        if (a.position !== undefined) return -1;
                        if (b.position !== undefined) return 1;
                        return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
                    })
                    .map((emp, idx) => ({
                        ...emp,
                        id: emp._id || emp.id,
                        image: emp.image || teamMembers[idx]?.image || lead1,
                        role: emp.profession || '',
                        bio: emp.bio || '',
                        team: emp.team || [],
                    }));
                setEmployees(sortedEmployees);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
            setEmployees([]);
            setFetchError(error?.message || 'Failed to load team members.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    // Merge employees with placeholders (fill up to 8 slots)
    const displayMembers = (() => {
        const result = [];
        
        // Use employees from database (up to 8)
        for (let i = 0; i < 8; i++) {
            if (employees[i]) {
                result.push({
                    ...employees[i],
                    isPlaceholder: false,
                });
            } else {
                result.push({
                    ...teamMembers[i],
                    isPlaceholder: true,
                });
            }
        }
        
        return result;
    })();

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                    }
                });
            },
            { threshold: 0.1 }
        );

        const sectionElement = sectionRef.current;
        if (sectionElement) {
            observer.observe(sectionElement);
        }

        return () => {
            if (sectionElement) {
                observer.unobserve(sectionElement);
            }
        };
    }, []);

    return (
        <>
            <style>{`
                .prose ul {
                    list-style-type: disc;
                    list-style-color: #E68A40;
                    padding-left: 1.5rem;
                    margin-top: 0.5rem;
                    margin-bottom: 0.5rem;
                }
                .prose ul li::marker {
                    color: #E68A40;
                }
                .prose ol {
                    list-style-type: decimal;
                    list-style-color: #E68A40;
                    padding-left: 1.5rem;
                    margin-top: 0.5rem;
                    margin-bottom: 0.5rem;
                }
                .prose ol li::marker {
                    color: #E68A40;
                }
                .prose li {
                    margin-top: 0.25rem;
                    margin-bottom: 0.25rem;
                    color: white;
                }
                .prose p {
                    margin-top: 0.5rem;
                    margin-bottom: 0.5rem;
                    color: white;
                }
                .prose strong {
                    font-weight: 600;
                    color: white;
                }
                .prose em {
                    font-style: italic;
                    color: white;
                }
                .prose u {
                    text-decoration: underline;
                }
                .prose a {
                    color: #E68A40;
                    text-decoration: underline;
                }
                .prose a:hover {
                    color: #D67A30;
                }
                .prose h1, .prose h2, .prose h3 {
                    color: white;
                    font-weight: 600;
                    margin-top: 1rem;
                    margin-bottom: 0.5rem;
                }
                .prose h1 {
                    font-size: 1.5rem;
                }
                .prose h2 {
                    font-size: 1.25rem;
                }
                .prose h3 {
                    font-size: 1.125rem;
                }
            `}</style>
        <section 
            ref={sectionRef}
            className={`bg-transparent transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{
                minHeight: isMobileView ? 'auto' : '100vh',
                height: isMobileView ? 'auto' : '100vh',
                paddingTop: isMobileView ? 'clamp(40px, 10vh, 80px)' : '80px',
                paddingBottom: isMobileView ? 'clamp(40px, 10vh, 100px)' : '100px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
            }}
        >
            <div className="w-full max-w-none mx-auto px-4 sm:px-6 md:px-10 lg:px-16 flex flex-col justify-center" style={{ height: isMobileView ? 'auto' : 'calc(100vh - 200px)', minHeight: isMobileView ? 0 : undefined }}>
                <div className={`text-center flex-shrink-0 ${isMobileView ? 'mb-4 sm:mb-6' : 'mb-8'}`}>
                    <h2 className={isMobileView ? "text-[28px] xs:text-[32px] sm:text-[38px] font-heading font-bold text-white tracking-[0.06em] mb-2 sm:mb-3 leading-tight px-2" : "text-[42px] sm:text-5xl lg:text-[52px] font-heading font-bold text-white tracking-[0.08em] mb-3"}>
                        <span className="text-[#E68A40]">THE PEOPLE BEHIND The</span><span className="text-white">Adox.com</span>
                    </h2>
                    <p className={isMobileView ? "text-sm sm:text-base text-white/85 max-w-[900px] mx-auto text-center px-4 leading-6 tracking-[0.03em]" : "text-[24px] text-white/85 max-w-[900px] mx-auto text-center px-4 lg:px-8 leading-8 tracking-[0.05em]"}>
                        Behind TheAdox.com is a team of professionals committed to delivering seamless and efficient support across industries.
                    </p>
                </div>

                {selectedMember ? (
                    <div
                        className="flex-1 flex items-center justify-center transition-all duration-500 ease-in-out animate-[fadeIn_0.5s_ease-in-out] px-2 sm:px-4"
                        style={{
                            minHeight: 0,
                            maxHeight: isMobileView ? 'none' : 'calc(100vh - 200px - 200px)',
                            marginTop: 0,
                        }}
                    >
                        <ExpandedMemberDetail 
                            member={selectedMember} 
                            onClose={() => {
                                setSelectedMember(null);
                                setExpandOrigin(null);
                                setExpandingIndex(null);
                            }}
                            cardRef={expandedCardRef}
                            expandOrigin={expandOrigin}
                            isMobile={isMobileView}
                            isTablet={isTablet}
                        />
                    </div>
                ) : (
                    <>
                        {fetchError && (
                            <div className="flex flex-col items-center gap-3 py-4 px-4 rounded-xl bg-amber-500/20 border border-amber-500/50 text-amber-200 max-w-xl mx-auto mb-4">
                                <p className="text-center text-sm sm:text-base">{fetchError}</p>
                                <button
                                    type="button"
                                    onClick={() => fetchEmployees()}
                                    className="px-4 py-2 rounded-lg bg-[#E68A40] text-white font-semibold hover:bg-[#D67A30] transition-colors"
                                >
                                    Retry
                                </button>
                            </div>
                        )}
                        {loading ? (
                            <div className="flex justify-center py-10">
                                <p className="text-white text-base sm:text-lg">Loading team members...</p>
                            </div>
                        ) : isMobileView ? (
                            <div className="flex flex-col items-center justify-center gap-4 w-full px-4">
                                {(() => {
                                    const member = displayMembers[mobileIndex] ?? displayMembers[0];
                                    if (!member) return null;
                                    return (
                                        <div 
                                            key={member.id || member.name || mobileIndex}
                                            data-people-behind-card="true"
                                            className={`group relative bg-white rounded-xl overflow-hidden shadow-[0_20px_32px_rgba(0,0,0,0.16)] transition-all duration-300 flex flex-col w-full ${isTablet ? 'max-w-md' : 'max-w-sm'} ${
                                                member.isPlaceholder ? '' : 'cursor-pointer active:scale-[0.98]'
                                            }`}
                                            style={isTablet ? { maxWidth: '28rem' } : undefined}
                                            ref={(el) => { cardRefs.current[mobileIndex] = el; }}
                                            onClick={() => {
                                                if (member.isPlaceholder) return;
                                                const rect = cardRefs.current[mobileIndex]?.getBoundingClientRect();
                                                if (rect) {
                                                    setExpandOrigin({
                                                        top: rect.top + window.scrollY,
                                                        left: rect.left + window.scrollX,
                                                        width: rect.width,
                                                        height: rect.height,
                                                    });
                                                } else setExpandOrigin(null);
                                                setExpandingIndex(mobileIndex);
                                                setSelectedMember(member);
                                            }}
                                        >
                                            <div 
                                                className="relative w-full overflow-hidden flex items-center justify-center rounded-t-xl"
                                                style={{
                                                    background: 'linear-gradient(180deg, #C4793C 0%, #C0783D 50%, #B96E35 78.83%, #AF602A 100%)',
                                                    padding: isMobileView ? '0.5rem 0 0' : '1rem',
                                                    minHeight: isMobileView ? 0 : '260px',
                                                    aspectRatio: isMobileView ? '1 / 1' : undefined
                                                }}
                                            >
                                                <img 
                                                    src={member.image} 
                                                    alt={member.name} 
                                                    className={isMobileView ? 'w-full h-full object-cover object-center' : 'w-full h-full object-contain object-center'}
                                                    style={{ objectPosition: 'center top', display: 'block' }}
                                                />
                                                <div 
                                                    className="absolute top-3 right-3 flex items-center justify-center px-2 py-1.5 rounded-md text-white"
                                                    style={{ background: 'rgba(175, 96, 42, 0.9)' }}
                                                >
                                                    <FaChevronRight className="w-3.5 h-3.5" aria-hidden />
                                                    <FaChevronRight className="w-3.5 h-3.5 -ml-1" aria-hidden />
                                                </div>
                                            </div>
                                            <div className="flex-shrink-0 px-4 pt-4 pb-4 text-center bg-white rounded-b-xl">
                                                <h3 className="text-base sm:text-lg font-extrabold text-[#121212] tracking-tight">{member.name}</h3>
                                                <p className="mt-1.5 text-sm text-[#5c6370] leading-snug">{member.role || member.profession}</p>
                                            </div>
                                        </div>
                                    );
                                })()}
                                <div className="flex items-center justify-center gap-3 sm:gap-4 mt-2">
                                    <button
                                        type="button"
                                        className="px-4 py-2 rounded-full bg-white/10 text-white border border-white/30 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                                        onClick={() => setMobileIndex((p) => Math.max(0, p - 1))}
                                        disabled={mobileIndex === 0}
                                    >
                                        Prev
                                    </button>
                                    <span className="text-white text-xs sm:text-sm font-medium tabular-nums">{mobileIndex + 1} / {displayMembers.length}</span>
                                    <button
                                        type="button"
                                        className="px-4 py-2 rounded-full bg-white/10 text-white border border-white/30 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                                        onClick={() => setMobileIndex((p) => Math.min(displayMembers.length - 1, p + 1))}
                                        disabled={mobileIndex >= displayMembers.length - 1}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4 gap-x-0 sm:gap-x-0 lg:gap-x-0 gap-y-1 sm:gap-y-1 lg:gap-y-1 items-center justify-items-center transition-all duration-300" style={{ height: 'calc(100vh - 200px - 200px)', columnGap: '4px', rowGap: '12px', paddingLeft: '283px', paddingRight: '283px' }}>
                                {displayMembers.map((member, index) => (
                                    <div 
                                        key={member.id || member.name || index}
                                        data-people-behind-card="true"
                                        className={`group relative bg-white rounded-[12px] overflow-hidden mx-auto shadow-[0_20px_32px_rgba(0,0,0,0.16)] transition-all duration-300 flex flex-col ${
                                            member.isPlaceholder ? '' : 'cursor-pointer hover:-translate-y-1 hover:scale-[1.02]'
                                        }`}
                                        style={{ 
                                            opacity: member.isPlaceholder ? 0.6 : 1,
                                            cursor: member.isPlaceholder ? 'default' : 'pointer',
                                            transform: expandingIndex === index ? 'scale(1.08)' : 'scale(1)',
                                            transition: 'transform 0.5s ease, opacity 0.4s ease',
                                            zIndex: expandingIndex === index ? 5 : 1,
                                            width: '100%',
                                            maxWidth: '280px',
                                            aspectRatio: '3/4',
                                            height: 'calc((100vh - 200px - 200px - 8px) / 2)',
                                            maxHeight: 'calc((100vh - 200px - 200px - 8px) / 2)'
                                        }}
                                        ref={(el) => (cardRefs.current[index] = el)}
                                        onClick={() => {
                                            if (member.isPlaceholder) return;
                                            const rect = cardRefs.current[index]?.getBoundingClientRect();
                                            if (rect) {
                                                setExpandOrigin({
                                                    top: rect.top + window.scrollY,
                                                    left: rect.left + window.scrollX,
                                                    width: rect.width,
                                                    height: rect.height,
                                                });
                                            } else setExpandOrigin(null);
                                            setExpandingIndex(index);
                                            setSelectedMember(member);
                                        }}
                                    >
                                        <div 
                                            className="relative w-full flex-1 overflow-hidden flex items-center justify-center rounded-t-[12px]"
                                            style={{
                                                background: 'linear-gradient(180deg, #C4793C 0%, #C0783D 50%, #B96E35 78.83%, #AF602A 100%)',
                                                padding: '0.5rem 0.5rem 0rem 0.5rem',
                                                minHeight: '78%'
                                            }}
                                        >
                                            <img 
                                                src={member.image} 
                                                alt={member.name} 
                                                className="w-full h-full object-contain object-center transition-transform duration-300 group-hover:scale-105"
                                                style={{ objectPosition: 'center top' }}
                                            />
                                            <div className="absolute top-2 right-2 flex items-center justify-center text-white drop-shadow-lg">
                                                <div className={`flex items-center gap-[2px] text-sm font-semibold transition-all duration-300 ${member.isPlaceholder ? 'text-white/40' : 'text-white/90 group-hover:text-[#ffd0a6]'}`}>
                                                    <FaChevronRight className="w-3 h-3" />
                                                    <FaChevronRight className="w-3 h-3 -ml-[6px]" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex-shrink-0 px-2 sm:px-3 py-1.5 sm:py-2 text-center flex flex-col justify-center bg-white rounded-b-[12px]" style={{ minHeight: '22%' }}>
                                            <h3 className="text-[12px] sm:text-[14px] lg:text-[16px] font-extrabold text-[#121212] tracking-tight leading-tight">{member.name}</h3>
                                            <p className="mt-0.5 text-[11px] sm:text-[12px] lg:text-[13px] text-[#5c6370] leading-snug">{member.role || member.profession}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </section>
        </>
    );
};

export default ThePeopleBehind;

